import { Button, Card, CardMedia, Container, Divider, Popover, Stack, Typography, colors, Box, TextField, InputLabel } from '@mui/material';
import React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import _ from 'lodash';
import { toast } from 'react-hot-toast';
import ShareIcon from '@mui/icons-material/Share';
import DownloadIcon from '@mui/icons-material/Download';
import SharingDialog from '../../components/SharingDialog';
import useQuery from '../../hooks/useQuery';
import Skeleton from 'react-loading-skeleton';
import { LoadingButton } from '@mui/lab';
import Cookies from 'universal-cookie';
import { fetchWith401Check } from '../../helpers/http';
import parse from 'html-react-parser';

// this will show the 1 page project to be generated into pdf
const Content = () => {
  const cookies = new Cookies();
  const params = useParams();
  const [loadingState, setLoadingState] = React.useState({ download: false, fetch: true });
  const [repository, setRepository] = React.useState({});
  const [sectors, setSectors] = React.useState({});
  const [anchor, setAnchor] = React.useState(null);
  const API_URL = process.env.REACT_APP_STAGE === 'DEVELOPMENT' ? process.env.REACT_APP_API_HOST : process.env.REACT_APP_API_HOST_PROD;
  const APP_URL = process.env.REACT_APP_STAGE === 'DEVELOPMENT' ? process.env.REACT_APP_HOST : process.env.REACT_APP_HOST_PROD;
  const downloadLinkRef = React.useRef(null);
  const downloadButtonRef = React.useRef(null);
  const [downloaderInfo, setDownloaderInfo] = React.useState({});
  const [locationInfo, setLocationInfo] = React.useState({ latitude: null, longitude: null });

  const handleLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setLocationInfo({ ...locationInfo, latitude, longitude });
        },
        (error) => {
          console.log(error);
        }
      );
    } else {
      console.log('Geolocation is not supported by this browser.');
    }
  };

  const query = useQuery();
  const isShare = query.get('share');

  const fetchRepositoryByID = async () => {
    if (!params.id) return;
    if (!_.isEmpty(repository)) return;

    setLoadingState({ ...loadingState, fetch: true });

    fetchWith401Check(`${API_URL}/tabledatas/summary/id?id=${params.id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${cookies.get('token')}` },
      referrer: 'no-referrer',
    })
      .then(function (response) {
        if (response.ok) {
          return response.json();
        }
        return Promise.reject(response);
      })
      .then(function (response) {
        const repoData = response?.response || {};
        setRepository({ ...repository, ...repoData });
        setLoadingState({ ...loadingState, fetch: false });
      })
      .catch(function (err) {
        setLoadingState({ ...loadingState, fetch: false });
        return toast.error(err.message || 'Something wrong happenned');
      });
  };
  React.useEffect(() => {
    if (params?.id) fetchRepositoryByID();
  }, [params]);

  React.useEffect(() => {
    handleLocation();
  }, []);

  const fetchSectors = async () => {
    if (!params.id) return;

    fetchWith401Check(`${API_URL}/tableprops/sectors`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${cookies.get('token')}` },
      referrer: 'no-referrer',
    })
      .then(function (response) {
        if (response.ok) {
          return response.json();
        }
        return Promise.reject(response);
      })
      .then(function (response) {
        const sectorsData = response?.responses || {};
        let _sectors = {};
        sectorsData.forEach((item) => {
          _sectors[item.key] = { label: item.label, label_id: item.label_id };
        });
        setSectors({ ...sectors, ..._sectors });
      })
      .catch(function (err) {
        return toast.error(err.message || 'Something wrong happenned');
      });
  };

  React.useEffect(() => {
    fetchSectors();
  }, []);

  const downloadPDF = async () => {
    setLoadingState({ ...loadingState, download: true });

    const _downloaderInfo = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      name: downloaderInfo.name,
      purpose: downloaderInfo.purpose,
      latitude: locationInfo.latitude,
      longitude: locationInfo.longitude,
      repository_id: repository.id,
    };

    if (!_downloaderInfo.name) return toast.error('Please fill your name/institution name');
    if (!_downloaderInfo.purpose) return toast.error('Please fill download purpose');

    fetchWith401Check(`${API_URL}/pdf/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${cookies.get('token')}` },
      body: JSON.stringify({
        website_url: `${APP_URL}/public/printonly/${params.id}`,
        downloader_info: _downloaderInfo,
      }),
      referrer: 'no-referrer',
    })
      .then(function (response) {
        if (response.ok) {
          toast.success('Download success');
          setLoadingState({ ...loadingState, download: false });
          setAnchor(null);
          return downloadLinkRef.current.click();
        }
        return Promise.reject(response);
      })

      .catch(function (err) {
        setLoadingState({ ...loadingState, download: false });
        return toast.error(err.message || 'Something wrong happenned');
      });
  };

  return (
    <Container>
      {loadingState.fetch && (
        <Stack direction='column' spacing={1} p={2}>
          <Skeleton height={100} />
          <Skeleton height={600} />
        </Stack>
      )}

      {repository.id && !loadingState.fetch && (
        <PreviewPage
          repository={repository}
          sectors={sectors}
          downloadPDF={downloadPDF}
          loadingState={loadingState}
          isShare={isShare}
          downloadButtonRef={downloadButtonRef}
          setAnchor={setAnchor}
        />
      )}

      {!repository.id && !loadingState.fetch && <IDNotFound />}

      <a ref={downloadLinkRef} style={{ display: 'none' }} href={`${API_URL}/download/pdf?id=${params.id}`} download={`${params.id}.pdf`}>
        Download PDF
      </a>

      <Popover
        id='Popup'
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{
          vertical: 'left',
          horizontal: 'left',
        }}
      >
        <Stack direction='column' sx={{ backgroundColor: colors.blue[400], height: 400, width: 400 }}>
          <Stack direction='column' sx={{ height: '100%', margin: 1, backgroundColor: 'white', padding: 2 }} spacing={2}>
            <Typography variant='h4' sx={{ textAlign: 'center', color: colors.blue[400] }}>
              Download
            </Typography>
            <Typography variant='body2' sx={{ textAlign: 'left' }}>
              Please fill these information below to start downloading this repository
            </Typography>
            <Divider />
            <TextField
              onChange={(e) => setDownloaderInfo({ ...downloaderInfo, name: e.currentTarget.value })}
              size='small'
              label='Your Name/Institution Name'
            />
            <TextField onChange={(e) => setDownloaderInfo({ ...downloaderInfo, purpose: e.currentTarget.value })} size='small' label='Download Purpose' />
            <Box sx={{ alignSelf: 'center', padding: 2 }}>
              <LoadingButton
                loading={loadingState.download}
                disabled={loadingState.download}
                onClick={downloadPDF}
                variant='contained'
                sx={{ backgroundColor: colors.amber[700] }}
              >
                {loadingState.download ? 'Downloading ...' : 'Download'}
              </LoadingButton>
            </Box>
          </Stack>
        </Stack>
      </Popover>
    </Container>
  );
};

const PreviewPage = ({ repository, sectors, downloadPDF, loadingState, isShare, downloadButtonRef, setAnchor }) => {
  const [showDialog, setShowDialog] = React.useState(isShare ? true : false);
  const doShare = () => {
    setShowDialog(true);
  };

  const htmlDecode = (input) => {
    var e = document.createElement('div');
    e.innerHTML = input;
    return e.childNodes.length === 0 ? '' : e.childNodes[0].nodeValue;
  };

  // if (isShare) doShare();
  return (
    <Stack direction='column' m={2} p={2} spacing={2}>
      {/* {JSON.stringify(repository.data)} */}
      <Typography sx={{ textAlign: 'center' }} variant='h2'>
        {repository?.data?.title}
      </Typography>

      <Divider />

      {/* {repository?.data?.thumbnail && (
        <Card elevation={0} sx={{ maxWidth: '100%', paddingTop: 2, paddingBottom: 2 }}>
          <CardMedia sx={{ height: 300 }} component='img' alt={repository.id} src={repository?.data?.thumbnail} />
        </Card>
      )} */}

      <Stack direction={{ sm: 'column', md: 'row' }} justifyContent='space-between' spacing={6}>
        <Stack direction='column' sx={{ flex: { sm: 1, md: 2 }, width: { md: '70%', sm: '100%' } }} spacing={2}>
          <Typography variant='h6'>
            Ringkasan <i>(Summary)</i>
          </Typography>
          {repository?.data?.ringkasan && <div dangerouslySetInnerHTML={{ __html: repository?.data?.ringkasan }} />}
          <Divider />
          <Typography variant='h6'>
            Dokumentasi <i>(Documentation)</i>
          </Typography>
          <Typography variant='body1'>{_.isEmpty(repository?.data?.dokumentasi) && '-'}</Typography>
          <Stack direction='column' spacing={2}>
            {repository?.data?.dokumentasi?.map((item) => (
              <Typography sx={{ cursor: 'pointer' }} onClick={() => window.open(item.path, '_blank')} variant='body2'>
                <CardMedia component='img' alt={item} src={item} />
              </Typography>
            ))}
          </Stack>
          <Typography variant='h6'>
            Lampiran <i>(Attachments)</i>
          </Typography>
          <Typography variant='body1'>{_.isEmpty(repository?.data?.attachments) && '-'}</Typography>
          <Stack direction='column' spacing={2}>
            {repository?.data?.attachments?.map((item, index) => (
              <Typography sx={{ cursor: 'pointer', wordWrap: 'break-word' }} onClick={() => window.open(item.path, '_blank')} variant='body2'>
                <b>
                  [{index + 1}]&nbsp;[{item.type} - {item.description}]
                </b>
                &nbsp;
                {/* {item.path.length > 50 ? item.path.substring(0, 47) + '...' : item.path} */}
                {item.path}
              </Typography>
            ))}
          </Stack>
        </Stack>
        <Stack direction='column' sx={{ flex: { sm: 1, md: 1 }, width: { md: '30%', sm: '100%' } }} spacing={2}>
          {/* JSON.stringify(repository) */}
          {/* <Entities repository={repository} /> */}
          {/* <Divider repository={repository} /> */}
          <YearPublication repository={repository} />
          <Divider />
          <Institution repository={repository} />
          <Divider />
          <SDGsImages repository={repository} />
          <Divider />
          {!_.isEmpty(sectors) && !_.isEmpty(repository)}
          <Sectors repository={repository} sectors={sectors} />
          <Divider />
          <LocationInfo repository={repository} />
          <Divider />
          {/* <ContactInformation repository={repository} />
          <Divider /> */}
          <DownloadAndShare
            repository={repository}
            downloadPDF={downloadPDF}
            loadingState={loadingState}
            share={doShare}
            downloadButtonRef={downloadButtonRef}
            setAnchor={setAnchor}
          />
        </Stack>
      </Stack>
      <SharingDialog open={showDialog} handleClose={() => setShowDialog(false)} title={repository?.data?.title} />
    </Stack>
  );
};

const DownloadAndShare = ({ downloadPDF, loadingState, share, downloadButtonRef, setAnchor }) => {
  return (
    <Stack direction={{ sm: 'column', md: 'row' }} flexWrap='wrap'>
      {loadingState.download ? (
        <Stack
          direction='row'
          spacing={1}
          sx={{ cursor: 'pointer', width: 120, height: 30, backgroundColor: colors.grey[200], marginBottom: 1, marginRight: 1 }}
          alignItems='center'
          justifyContent='center'
        >
          <DownloadIcon fontSize='small' sx={{ color: 'white' }} />
          <Typography variant='body2' sx={{ color: colors.grey }}>
            Loading ...
          </Typography>
        </Stack>
      ) : (
        <Stack
          direction='row'
          spacing={1}
          sx={{ cursor: 'pointer', width: 120, height: 30, backgroundColor: colors.blue[300], marginBottom: 1, marginRight: 1 }}
          alignItems='center'
          justifyContent='center'
          // onClick={() => downloadPDF()}
          ref={downloadButtonRef}
          onClick={(e) => setAnchor(e.currentTarget)}
        >
          <DownloadIcon fontSize='small' sx={{ color: 'white' }} />
          <Typography variant='body2' sx={{ color: 'white' }}>
            Download
          </Typography>
        </Stack>
      )}

      <Stack
        onClick={share}
        direction='row'
        spacing={1}
        sx={{ cursor: 'pointer', width: 120, height: 30, backgroundColor: colors.blue[300] }}
        alignItems='center'
        justifyContent='center'
      >
        <ShareIcon fontSize='small' sx={{ color: 'white' }} />
        <Typography variant='body2' sx={{ color: 'white' }}>
          Share
        </Typography>
      </Stack>
    </Stack>
  );
};

const IDNotFound = () => {
  return (
    <Stack direction='column' alignItems='center'>
      <Typography>ID not found, please select the correct document.</Typography>
    </Stack>
  );
};

const Entities = ({ repository }) => {
  return (
    <Stack direction='column'>
      <Typography variant='h6'>Entities</Typography>
      <Typography>Entity 1.</Typography>
    </Stack>
  );
};

const Institution = ({ repository }) => {
  return (
    <Stack direction='column'>
      <Typography variant='h6'>Institution</Typography>
      <Stack direction='column'>
        {!_.isEmpty(repository?.data?.institution_agency) &&
          repository?.data?.institution_agency?.map((item, index) => <Typography variant='body2'>{item?.institution?.label}</Typography>)}
      </Stack>
    </Stack>
  );
};

const YearPublication = ({ repository }) => {
  return (
    <Stack direction='column'>
      <Typography variant='h6'>Year: 2022</Typography>
    </Stack>
  );
};

const SDGsImages = ({ repository }) => {
  return (
    <Stack direction='column'>
      <Typography variant='h6'>SDG Goals</Typography>
      <Stack direction='row' flexWrap='wrap' spacing={1}>
        {!_.isEmpty(repository?.data?.sdg_goal) &&
          repository?.data?.sdg_goal?.map((item, index) => (
            <img key={index} style={{ width: 60, margin: 4 }} src={`/assets/sdg_goal_${item.split('_')[1]}.jpeg`} />
          ))}
      </Stack>
    </Stack>
  );
};

const Sectors = ({ repository, sectors }) => {
  return (
    <Stack direction='column' spacing={2}>
      <Typography variant='h6'>Sectors</Typography>

      {!_.isEmpty(repository?.data?.sector) &&
        repository?.data?.sector?.map((item, index) => (
          <Stack direction='column' spacing={1} key={index}>
            <Typography sx={{ fontWeight: 'bold' }}>Sector {index + 1}</Typography>
            <Typography variant='body2'>{sectors[item]?.label}</Typography>
          </Stack>
        ))}
    </Stack>
  );
};

const LocationInfo = ({ repository }) => {
  return (
    <Stack direction='column'>
      <Typography variant='h6'>Location</Typography>
      {!_.isEmpty(repository?.data?.location) &&
        repository?.data?.location?.map((item, index) => (
          <Stack direction='row' key={index}>
            <Typography variant='body2'>
              {item?.province?.label}, {item?.city?.label}
            </Typography>
          </Stack>
        ))}
    </Stack>
  );
};

const ContactInformation = ({ repository }) => {
  return (
    <Stack direction='column'>
      <Typography variant='h6'>Contact Information:</Typography>
      <Typography>{repository?.data?.email || '-'}</Typography>
    </Stack>
  );
};

export default Content;
